import { FirebaseApp, initializeApp } from 'firebase/app';
import {
  Auth,
  confirmPasswordReset,
  createUserWithEmailAndPassword,
  FacebookAuthProvider,
  getAuth,
  GoogleAuthProvider,
  OAuthProvider,
  onAuthStateChanged,
  signInWithCredential,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  UserCredential,
  verifyPasswordResetCode,
} from 'firebase/auth';
import {
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  serverTimestamp,
  setLogLevel,
  terminate,
  waitForPendingWrites,
} from 'firebase/firestore';
import { TFunction } from 'next-i18next';
import Router from 'next/router';
import { MutableRefObject } from 'react';
import { TStore } from '@bridebook/web/store/store';
import { Users, Weddings } from '@bridebook/models';
import { getApp, getFirestore } from '@bridebook/models/source/firebase/firestore';
import { fetchy } from '@bridebook/web/lib/fetchy';
import gazetteer, { CountryCodes } from '@bridebook/toolbox/src/gazetteer';
import { SentryMinimal } from '@bridebook/toolbox/src/sentry';
import { User as FirebaseUser } from '@firebase/auth';
import { clearIndexedDbPersistence } from '@firebase/firestore';
import {
  AuthStateType,
  LOGOUT_DELAY_TIME,
  TAuthRequest,
  TRedirectAfterLoginOrSignup,
} from '@bridebook/web/components/auth/auth-context';
import { acceptedCollaborationInvite } from '@bridebook/web/lib/access-control';
import {
  AuthSessionData,
  LoginOrSignupRequest,
  LoginOrSignupResult,
} from '@bridebook/web/lib/api/authenticate/types';
import type { CaptchaRequest } from '@bridebook/web/lib/api/with-captcha';
import {
  changePasswordError,
  changePasswordSuccess,
  firebaseSignOut,
  isSignedOutFirebase,
  loginSuccessAnalytics,
  redirectAfterAuthSuccess,
  registrationSuccessAnalytics,
  saveNextPathAfterOnboarding,
  signInError,
  signInOrSignUpError,
  signOutCompleted,
  signUpError,
  userSetupCompleted,
  userSetupStarted,
} from '@bridebook/web/lib/auth/actions';
import {
  AuthBridebookError,
  AuthProviders,
  ICredentialsFields,
} from '@bridebook/web/lib/auth/types';
import {
  checkExistingProviders,
  getAuthProvider,
} from '@bridebook/web/lib/auth/utils/auth-provider-utils';
import { fetchBudgetData } from '@bridebook/web/lib/auth/utils/fetch-budget-data';
import { fetchChecklistData } from '@bridebook/web/lib/auth/utils/fetch-checklist-data';
import { fetchGuestlistData } from '@bridebook/web/lib/auth/utils/fetch-guestlist-data';
import { fetchShortlistData } from '@bridebook/web/lib/auth/utils/fetch-shortlist-data';
import { getDocFromCacheOrServer } from '@bridebook/web/lib/auth/utils/get-doc-from-cache-or-server';
import { getRedirect } from '@bridebook/web/lib/auth/utils/redirects';
import { updateLocale } from '@bridebook/web/lib/auth/utils/update-locale';
import { validateEmailAndPassword } from '@bridebook/web/lib/auth/utils/validate-email-pass';
import { toggleSnackbar } from '@bridebook/web/lib/bbcommon/actions';
import { onFirebaseBudget } from '@bridebook/web/lib/budget/actions/on-listener';
import { changeLanguage } from '@bridebook/web/lib/change-language/utils/change-language';
import {
  fetchTasksInitialSuccess,
  fetchUserChecklistSuccess,
} from '@bridebook/web/lib/checklist/actions';
import { env } from '@bridebook/web/lib/env';
import { getI18n } from '@bridebook/web/lib/i18n/getI18n';
import { getCookieLocale } from '@bridebook/web/lib/i18n/utils/get-cookie-locale';
import nativeLogin from '@bridebook/web/lib/mobile-app/utils/native-social-auth';
import { fetchUserShortlistSuccess } from '@bridebook/web/lib/shortlist/actions';
import { mapWeddingSuppliersSnapshotToShortlistData } from '@bridebook/web/lib/shortlist/utils/map-wedding-suppliers-snapshot-to-shortlist-data';
import { ROOT_TODAY_TASK_FLOW_ID } from '@bridebook/web/lib/task/today-task-flow';
import { TodayTaskStorage } from '@bridebook/web/lib/task/today-task-storage';
import type { BridebookError, IUserSerialized, TWeddingSerialized } from '@bridebook/web/lib/types';
import { GuestsOnlyUrls, UrlHelper } from '@bridebook/web/lib/url-helper';
import {
  markUserAsDeletedError,
  markUserAsDeletedStart,
  markUserAsDeletedSuccess,
} from '@bridebook/web/lib/users/actions/mark-user-as-deleted';
import { isCordovaApp, recursiveOmitMutableTimestamps } from '@bridebook/web/lib/utils';
import { getAsPathNormalized } from '@bridebook/web/lib/utils/url';
import validate from '@bridebook/web/lib/validate';
import { getisIOS } from '@bridebook/web/lib/utils/get-is-ios';
import { recursiveSerializeTimestamps } from '@bridebook/toolbox/src/serialize-timestamps';

export interface ISuccessCallbackParams {
  token: string;
}

const earlyInitializeBasedOnlyOnUserId = (userId: string) => {
  SentryMinimal().setUser({ id: userId });
  const todayTaskStorage = TodayTaskStorage({ id: ROOT_TODAY_TASK_FLOW_ID }, userId);
  todayTaskStorage.migrate();
};

const handleUserLoaded = async (user: IUserSerialized) => {
  const cookieLocale = getCookieLocale();
  const userLocale = user.l10n.locale;
  if (!cookieLocale || cookieLocale !== userLocale) {
    await changeLanguage(userLocale);
  }
};
/**
 * Redirects user either to the home page or onboarding based on the fact
 * whether the user just created an account or logged in
 */
export const handleRedirectAfterAuth = async (store: TStore, authSessionData: AuthSessionData) => {
  const { isCollaborator, signedUp, weddingData, userData } = authSessionData;
  const dispatch = store.dispatch;
  try {
    const market = gazetteer.getMarketByCountry(weddingData.l10n.country);
    market.locale = userData.l10n.locale;

    const { pathname, query } = Router;
    const asPath = getAsPathNormalized();
    const state = store.getState();
    const {
      app: { previousQuery },
      enquiries: { loggedOutEnquiryTriggered },
    } = state;
    const { path, redirect, next } = getRedirect({
      query,
      asPath,
      signedUp,
      pathname,
      previousQuery,
      market,
      loggedOutEnquiryTriggered,
      ...(authSessionData.isCollaborator
        ? {
            isCollaborator: true,
            redirectURLAfterLoginOrSignup: authSessionData.redirect,
          }
        : {
            isCollaborator: false,
          }),
    });

    if (next) {
      dispatch(saveNextPathAfterOnboarding(encodeURI(next)));
    }

    redirect &&
      (await updateLocale(market, path, () => {
        window.scrollTo(0, 0);
      }));
    dispatch(redirectAfterAuthSuccess());
  } catch (error) {
    dispatch({
      type: 'REDIRECT_AFTER_AUTH_ERROR',
      payload: { error },
    });
    SentryMinimal().captureException(error, {
      tags: {
        source: 'Auth context',
        feature: 'redirectAfterLoginOrSignup',
      },
      extra: {
        signedUp,
        isCollaborator,
      },
    });
  }
};

/**
 * Initializes the user and the services and managers such as sentry or today's task local storage manager.
 * It runs only once per app start (this includes hard page reload as well)
 */
export const handleInitialize = (
  store: TStore,
  loginOrRegisterInProgress: MutableRefObject<boolean>,
  authStateRef: MutableRefObject<AuthStateType>,
  handleSetAuthState: (value: AuthStateType) => void,
  firebaseApp?: FirebaseApp,
) => {
  const dispatch = store.dispatch;
  try {
    onAuthStateChanged(getAuth(firebaseApp), async (firebaseUser) => {
      const path = window.location.pathname;
      const i18n = getI18n();

      if (loginOrRegisterInProgress.current || authStateRef.current.isInitialized) {
        return;
      }
      if (firebaseUser) {
        dispatch(userSetupStarted());
        earlyInitializeBasedOnlyOnUserId(firebaseUser.uid);
        let weddingData: TWeddingSerialized | null | undefined = null;

        const userDataRef = Users._.getById(firebaseUser.uid).reference;
        const userDataRaw = (await getDocFromCacheOrServer(userDataRef)).data();
        const userData = userDataRaw
          ? recursiveOmitMutableTimestamps(recursiveSerializeTimestamps(userDataRaw))
          : undefined;

        if (userData && userData.weddings) {
          const weddingDataRef = Weddings._.getById(
            userData.weddings[userData.weddings.length - 1],
          ).reference;
          const weddingDataRaw = (await getDocFromCacheOrServer(weddingDataRef)).data();
          weddingData = weddingDataRaw
            ? recursiveOmitMutableTimestamps(recursiveSerializeTimestamps(weddingDataRaw), [
                'draft',
              ])
            : undefined;
        }

        if (!weddingData || !userData) {
          SentryMinimal().captureMessage(
            '[Auth context] User not found in DB or user does not have an active wedding',
            { extra: { userData, weddingData } },
          );
          dispatch(toggleSnackbar('alert', i18n.t('auth:noWeddingFound.contactSupport')));
          setTimeout(() => {
            handleLogout(store, false, firebaseApp);
          }, LOGOUT_DELAY_TIME);
          return;
        }
        await handleUserLoaded(userData);
        dispatch(userSetupCompleted(firebaseUser, userData, weddingData));

        try {
          await Promise.all([
            fetchChecklistData(dispatch, weddingData.id, weddingData.l10n.country),
            fetchShortlistData(dispatch, weddingData.id),
            fetchGuestlistData(dispatch, weddingData.id),
            fetchBudgetData(dispatch, weddingData.id),
          ]);
        } catch (error) {
          SentryMinimal().captureException(error, {
            tags: { source: 'Auth context', feature: 'initialDataFetch' },
          });
        }

        handleSetAuthState({
          isInitialized: true,
          isAuthenticated: true,
          user: userData,
          wedding: weddingData,
        });

        const isSignupPage = path.includes(UrlHelper.signup);
        if (isSignupPage) {
          const response = await fetchy().authenticated.post('/api/auth/user-session', null, null);
          if (response.found) {
            await handleRedirectAfterAuth(store, {
              signedUp: false,
              isCollaborator: false,
              weddingData,
              userData,
            });
          } else {
            dispatch(toggleSnackbar('alert', i18n.t('auth:sessionExpired')));
            await handleLogout(store, false, firebaseApp);
          }
          return;
        }

        if (path !== '/') {
          const marketFromUrl = gazetteer.getMarketByURL(path, CountryCodes.GB);
          if (weddingData.l10n.country !== marketFromUrl.country) {
            await handleRedirectAfterAuth(store, {
              signedUp: false,
              isCollaborator: false,
              weddingData,
              userData,
            });
          }
        }
      } else {
        dispatch(isSignedOutFirebase());
        const response = await fetchy().authenticated.post('/api/auth/user-session', null, null);
        if (response.found) {
          dispatch(toggleSnackbar('alert', i18n.t('auth:sessionExpired')));

          try {
            await fetchy().with({ credentials: 'same-origin' }).post('/api/auth/logout', null, {});

            const isGuestOnlyUrl = Object.values(GuestsOnlyUrls).some((url) => path.includes(url));

            if (!isGuestOnlyUrl || (isCordovaApp() && path !== '/')) {
              await Router.push(UrlHelper.login);
            }
          } catch (error) {
            // Noop
          }
        }

        handleSetAuthState({
          isInitialized: true,
          isAuthenticated: false,
          user: null,
          wedding: null,
        });
      }
    });
  } catch (error) {
    SentryMinimal().captureException(error, {
      tags: {
        source: 'Auth context',
        feature: 'initialize',
      },
    });
  }
};

/**
 * Calls an authenticate endpoint that depending on the payload creates / reads / modifies user and wedding data
 * from / in firebase whether the user just created an account / logged in into existing account or joins a wedding as
 * a collaborator with new or existing account.
 * On success, the most recent user and wedding data is saved in store and in indexDB to speed up the "initialize" process
 * the next time the app is started.
 * On fail, no data is modified / created in the database, new account is deleted and in the case of the existing one it is logged out.
 * The notice is also displayed to the user that something went wrong and suggestion on what to do next (contact support or try again)
 */
export const handleSetupUserAfterLoginOrSignup = async (
  store: TStore,
  firebaseUser: FirebaseUser,
  handleSetAuthState: (value: AuthStateType) => void,
  logout: (logOutAllDevices?: boolean) => Promise<void>,
  countryCode: CountryCodes,
  locale: string,
  nonceId?: string,
  nonceSecret?: string,
  captchaToken?: string,
) => {
  const dispatch = store.dispatch;
  dispatch(userSetupStarted());
  earlyInitializeBasedOnlyOnUserId(firebaseUser.uid);
  const state = store.getState();
  const pathName = state.app.pathname;

  if (!captchaToken) {
    throw new Error('reCAPTCHA token is required');
  }

  const fetchBody: LoginOrSignupRequest & CaptchaRequest = {
    countryCode,
    locale,
    nonceId,
    nonceSecret,
    captchaToken,
  };

  const response = await fetchy()
    .authenticated.with({ credentials: 'same-origin' })
    .post('/api/auth/authenticate', null, fetchBody);

  if (response.status === 'success') {
    const {
      userData,
      weddingData,
      weddingSuppliersData,
      countryTasksData,
      weddingTasksData,
      weddingCostsData,
      geolocationCountryCode,
    } = response;

    await handleUserLoaded(userData);
    dispatch(userSetupCompleted(firebaseUser, userData, weddingData));

    if (response.signedUp) {
      dispatch(
        registrationSuccessAnalytics(userData, pathName, locale, countryCode, {
          geolocationCountryCode,
        }),
      );
    } else {
      dispatch(loginSuccessAnalytics(userData, pathName));
    }

    // @ts-ignore FIXME
    dispatch(fetchTasksInitialSuccess(countryTasksData));
    dispatch(
      fetchUserChecklistSuccess({
        result: weddingTasksData,
        source: 'server',
      }),
    );

    const shortlisted = await mapWeddingSuppliersSnapshotToShortlistData(weddingSuppliersData);
    dispatch(fetchUserShortlistSuccess({ shortlisted }));
    dispatch(onFirebaseBudget(weddingCostsData));
    if (response.isCollaborator) {
      dispatch(acceptedCollaborationInvite());
    }

    handleSetAuthState({
      isInitialized: true,
      isAuthenticated: true,
      user: userData,
      wedding: weddingData,
    });
  }

  if (response.status === 'fail' && response.flowType === 'signup') {
    SentryMinimal().captureMessage('[Auth context] Signup failed', {
      tags: { source: 'Auth context', feature: 'setupUserAfterLoginOrSignup' },
      extra: { responseData: response },
    });
    const error: AuthBridebookError = {
      prop: 'email',
      name: '',
      code: 'custom/api-authenticate-failed',
      message: '',
    };
    dispatch(signUpError(error, { email: firebaseUser.email ?? 'no email provided' }));
    return;
  }

  if (response.status === 'fail' && response.flowType === 'login') {
    SentryMinimal().captureMessage('[Auth context] Login failed', {
      tags: { source: 'Auth context', feature: 'setupUserAfterLoginOrSignup' },
      extra: { responseData: response },
    });
    const error: BridebookError = {
      prop: 'email',
      name: '',
      code: 'custom/api-authenticate-failed',
      message: '',
    };
    dispatch(signInError(error));
    await logout();
  }

  if (response.status === 'fail' && response.flowType === 'uninitialized') {
    SentryMinimal().captureMessage('[Auth context] Api failed before the flow type was checked', {
      tags: { source: 'Auth context', feature: 'setupUserAfterLoginOrSignup' },
      extra: { responseData: response },
    });
    const error: BridebookError = {
      prop: 'email',
      name: '',
      code: 'custom/api-authenticate-failed',
      message: '',
    };
    dispatch(signInOrSignUpError(error));
    await logout();
    return;
  }

  return response;
};

/**
 * Handles login and register with auth providers on cordova devices
 */
export const handleCordovaOauth = (
  providerId: AuthProviders,
  firebaseAuth: Auth,
): Promise<UserCredential> =>
  new Promise<UserCredential>((resolve, reject) => {
    const successCallback = ({ token }: ISuccessCallbackParams) => {
      let credential;

      switch (providerId) {
        case AuthProviders.FACEBOOK:
          credential = FacebookAuthProvider.credential(token);
          break;
        case AuthProviders.GOOGLE:
          credential = GoogleAuthProvider.credential(token);
          break;
        case AuthProviders.APPLE:
          credential = new OAuthProvider('apple.com').credential({
            idToken: token,
          });
          break;
        default:
          reject('Not supported provider');
          return;
      }
      signInWithCredential(firebaseAuth, credential)
        .then((response) => {
          resolve(response);
        })
        .catch((error) => {
          reject(error);
        });
    };

    const errorCallback = (error: any) => {
      reject(error);
    };

    nativeLogin({
      providerId,
      successCallback,
      errorCallback,
    });
  });

/**
 * Logs into an existing firebase-auth account with email and password provider only
 */
export const handleLogin = async (
  store: TStore,
  authRequest: TAuthRequest,
  loginOrRegisterInProgress: MutableRefObject<boolean>,
  setupUserAfterLoginOrSignup: (
    firebaseUser: FirebaseUser,
    countryCode: CountryCodes,
    locale: string,
  ) => Promise<LoginOrSignupResult | undefined>,
) => {
  const dispatch = store.dispatch;
  try {
    loginOrRegisterInProgress.current = true;
    if (authRequest.type === 'password') {
      const firebaseApp = getApp();
      const auth = getAuth(firebaseApp);
      await validateEmailAndPassword(validate, authRequest);
      await checkExistingProviders(auth, authRequest.email, AuthProviders.PASSWORD);
      const userCredential = await signInWithEmailAndPassword(
        auth,
        authRequest.email.trim().toLowerCase(),
        authRequest.password,
      );

      return await setupUserAfterLoginOrSignup(
        userCredential.user,
        authRequest.countryCode,
        authRequest.locale,
      );
    }
  } catch (e) {
    if (authRequest.type === 'password') {
      dispatch(signInError(e, AuthProviders.PASSWORD));
    }
  } finally {
    loginOrRegisterInProgress.current = false;
  }
};

/**
 * Clears all data stored in indexDB and terminates the firestore connection
 * after the user logged out
 */
export const handleLogout = async (
  store: TStore,
  logOutAllDevices?: boolean,
  firebaseApp?: FirebaseApp,
) => {
  const dispatch = store.dispatch;
  try {
    const user = getAuth(firebaseApp).currentUser;
    if (!user) {
      throw new Error('No user in state');
    }

    const firestore = getFirestore();
    await waitForPendingWrites(firestore);

    /**
     * Close listeners before the client is torn down. An open one can be called
     * back on a dead connection and break Firestore for the rest of the session.
     * Runs after the pending writes await, which can stall offline.
     */
    dispatch(firebaseSignOut());

    await signOut(getAuth(firebaseApp));
    await terminate(firestore);
    await clearIndexedDbPersistence(firestore);

    if (!firebaseApp) {
      firebaseApp = initializeApp(env.FIREBASE);
    }

    const isIOS = getisIOS();

    /**
     * To enable fetching from cache after logging out and back in, we need to re-initialize the offline Firebase persistence after it has been cleared.
     */
    initializeFirestore(firebaseApp, {
      experimentalAutoDetectLongPolling: true,
      experimentalLongPollingOptions: {
        timeoutSeconds: 25,
      },
      ignoreUndefinedProperties: true,
      ...(!isIOS && {
        localCache: persistentLocalCache({
          cacheSizeBytes: 50 * 1024 ** 2, // 50MB
          tabManager: persistentMultipleTabManager(),
        }),
      }),
    });

    setLogLevel('error');

    try {
      await fetchy().with({ credentials: 'same-origin' }).post('/api/auth/logout', null, {
        logOutAllDevices,
      });
    } catch (error) {
      // Noop
    }

    dispatch(signOutCompleted());
  } catch (error) {
    SentryMinimal().captureException(error, {
      tags: {
        source: 'Auth context',
        feature: 'logout',
      },
      extra: {
        logOutAllDevices,
      },
    });
  }
};

/**
 * Creates a new firebase-auth account with email and password or social providers
 * or
 * logs into an existing firebase-auth account with social providers
 */
export const handleRegister = async (
  store: TStore,
  authRequest: TAuthRequest,
  loginOrRegisterInProgress: MutableRefObject<boolean>,
  setupUserAfterLoginOrSignup: (
    firebaseUser: FirebaseUser,
    countryCode: CountryCodes,
    locale: string,
  ) => Promise<LoginOrSignupResult | undefined>,
  cordovaOauth: (providerId: AuthProviders, firebaseAuth: Auth) => Promise<UserCredential>,
  firebaseApp?: FirebaseApp,
) => {
  const dispatch = store.dispatch;
  try {
    loginOrRegisterInProgress.current = true;
    if (authRequest.type === 'password') {
      const firebaseApp = getApp();
      const auth = getAuth(firebaseApp);
      await validateEmailAndPassword(validate, authRequest);
      await checkExistingProviders(auth, authRequest.email, AuthProviders.PASSWORD);

      let userCredential: UserCredential;

      try {
        userCredential = await createUserWithEmailAndPassword(
          auth,
          authRequest.email.trim().toLowerCase(),
          authRequest.password,
        );
      } catch (error) {
        if (error.code !== 'auth/email-already-in-use') {
          throw error;
        }

        /**
         * If email is already in use, we try to log in the user using the provided credentials.
         */
        userCredential = await signInWithEmailAndPassword(
          auth,
          authRequest.email.trim().toLowerCase(),
          authRequest.password,
        ).catch(() => {
          /**
           * If sign in also fails, we throw the original error from createUserWithEmailAndPassword.
           */
          throw error;
        });
      }

      return await setupUserAfterLoginOrSignup(
        userCredential.user,
        authRequest.countryCode,
        authRequest.locale,
      );
    }

    if (authRequest.type === 'magic') {
      const userCredential = await signInWithCustomToken(getAuth(firebaseApp), authRequest.token);

      return await setupUserAfterLoginOrSignup(
        userCredential.user,
        authRequest.countryCode,
        authRequest.locale,
      );
    }
    if (authRequest.type === 'social') {
      const auth = getAuth(firebaseApp);
      const provider = getAuthProvider(authRequest.provider);

      const userCredential = isCordovaApp()
        ? await cordovaOauth(authRequest.provider, auth)
        : await signInWithPopup(auth, provider);

      return await setupUserAfterLoginOrSignup(
        userCredential.user,
        authRequest.countryCode,
        authRequest.locale,
      );
    }
  } catch (e) {
    if (authRequest.type === 'password') {
      dispatch(signUpError(e, authRequest));
    } else {
      dispatch(
        signInError(
          {
            ...e,
            ...(authRequest.type === 'social' && {
              provider: e?.customData?._tokenResponse?.verifiedProvider?.[0],
            }),
          },
          authRequest.type === 'social' ? authRequest.provider : undefined,
        ),
      );
    }
  } finally {
    loginOrRegisterInProgress.current = false;
  }
};

/**
 * Marks user as deleted in the firebase and logs the user out
 */
export const handleDeleteAccount = async (
  store: TStore,
  logout: (logOutAllDevices?: boolean) => Promise<void>,
  t: TFunction,
) => {
  const dispatch = store.dispatch;
  const state = store.getState();
  const userId = state.users.user?.id;
  if (!userId) throw new Error('Missing userId');
  if (userId) {
    try {
      dispatch(markUserAsDeletedStart);
      await Users._.getById(userId).set({
        deleteAt: serverTimestamp(),
      });
      dispatch(markUserAsDeletedSuccess);
      setTimeout(() => {
        logout(true);
      }, LOGOUT_DELAY_TIME);
    } catch (error) {
      dispatch(toggleSnackbar('alert', t('settings:deleteAccount.errorSnackbarMessage')));
      dispatch(markUserAsDeletedError);
      SentryMinimal().captureException(error, {
        tags: {
          source: 'Auth context',
          feature: 'deleteAccount',
        },
      });
    }
  }
};

/**
 * [coupleside and cms] Changes user password in firebase-auth and on success
 * logs in the coupleside users or redirects to the continueUrl if the url was passed (mainly cms login page url for cms users)
 */
export const handleChangePassword = async (
  store: TStore,
  actionCode: string,
  authFields: ICredentialsFields,
  login: (authRequest: TAuthRequest) => Promise<LoginOrSignupResult | undefined>,
  redirectAfterLoginOrSignup: TRedirectAfterLoginOrSignup,
  countryCode: CountryCodes,
  locale: string,
  continueUrl?: string,
) => {
  const dispatch = store.dispatch;
  try {
    const firebaseApp = getApp();
    const defaultAuth = getAuth(firebaseApp);
    const actualEmail = await verifyPasswordResetCode(defaultAuth, actionCode);
    await confirmPasswordReset(defaultAuth, actionCode, authFields.password);

    const fields: TAuthRequest = {
      type: 'password',
      email: actualEmail,
      password: authFields.password,
      countryCode,
      locale,
    };

    if (continueUrl) {
      window.location.replace(continueUrl);
    } else {
      const result = await login(fields);
      if (result && result.status === 'success') {
        dispatch(changePasswordSuccess(fields));
        await redirectAfterLoginOrSignup(result);
      }
    }
  } catch (error) {
    dispatch(changePasswordError(authFields, error));
  }
};
